<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <oxd-dialog
    :style="{width: '90%', maxWidth: '680px'}"
    @update:show="onCancel"
  >
    <div class="orangehrm-modal-header">
      <oxd-text type="card-title">
        {{ $t('time.accept_claims_title') }}
      </oxd-text>
    </div>
    <oxd-divider />

    <div class="claim-dialog-body">
      <oxd-text tag="p">
        {{ count }} {{ $t('time.pending_claim_count') }}
      </oxd-text>

      <oxd-text
        v-if="outsidePeriodCount > 0"
        tag="p"
        class="claim-outside-warning"
      >
        ⚠️ {{ outsidePeriodCount }} {{ $t('time.claim_outside_period_count') }}
      </oxd-text>

      <table v-if="items.length > 0" class="claim-table">
        <thead>
          <tr>
            <th>{{ $t('general.employee') }}</th>
            <th>{{ $t('time.claim_reference') }}</th>
            <th class="claim-numeric">{{ $t('time.claim_expense_count') }}</th>
            <th class="claim-numeric">{{ $t('general.amount') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in items" :key="item.requestId">
            <td>{{ item.employeeName }}</td>
            <td>
              {{ item.referenceId }}
              <span
                v-if="item.hasExpensesOutsidePeriod"
                class="claim-outside-marker"
                :title="$t('time.claim_outside_period')"
                >⚠️</span
              >
            </td>
            <td class="claim-numeric">{{ item.expenseCount }}</td>
            <td class="claim-numeric">
              {{ item.amount.toFixed(2) }} {{ item.currency }}
            </td>
          </tr>
        </tbody>
      </table>

      <oxd-text v-if="count > items.length" tag="p" class="claim-more">
        + {{ count - items.length }} {{ $t('time.not_listed') }}
      </oxd-text>

      <div class="claim-totals">
        <oxd-text
          v-for="(amount, currency) in totals"
          :key="currency"
          tag="p"
          class="claim-total-row"
        >
          {{ $t('general.total') }}: {{ amount.toFixed(2) }} {{ currency }}
        </oxd-text>
      </div>
    </div>

    <oxd-divider />
    <oxd-form-actions class="orangehrm-form-action">
      <oxd-button
        display-type="ghost"
        :label="$t('general.cancel')"
        @click="onCancel"
      />
      <oxd-button
        display-type="secondary"
        :label="$t('time.accept_all_claims')"
        :disabled="isLoading"
        @click="onConfirm"
      />
    </oxd-form-actions>
  </oxd-dialog>
</template>

<script>
import {OxdDialog} from '@ohrm/oxd';

export default {
  name: 'ApprovePendingClaimDialog',

  components: {
    'oxd-dialog': OxdDialog,
  },

  props: {
    count: {
      type: Number,
      required: true,
    },
    totals: {
      type: Object,
      default: () => ({}),
    },
    items: {
      type: Array,
      default: () => [],
    },
    outsidePeriodCount: {
      type: Number,
      default: 0,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['close', 'confirm'],

  methods: {
    onCancel() {
      this.$emit('close');
    },
    onConfirm() {
      this.$emit('confirm');
    },
  },
};
</script>

<style lang="scss" scoped>
.claim-dialog-body {
  padding: 0.75rem 0;
  max-height: 340px;
  overflow-y: auto;
}

.claim-outside-warning {
  margin-top: 0.4rem;
  font-size: 0.8rem;
  color: #b06d00;
}

.claim-table {
  width: 100%;
  margin-top: 0.75rem;
  border-collapse: collapse;
  font-size: 0.8rem;

  th,
  td {
    text-align: left;
    padding: 0.35rem 0.5rem;
    border-bottom: 1px solid #e0e0e0;
  }

  th {
    font-weight: 600;
  }
}

.claim-numeric {
  text-align: right;
}

.claim-outside-marker {
  margin-left: 0.25rem;
  cursor: help;
}

.claim-more {
  margin-top: 0.5rem;
  font-size: 0.8rem;
  font-style: italic;
}

.claim-totals {
  margin-top: 0.75rem;
  text-align: right;
}

.claim-total-row {
  font-weight: 600;
  font-size: 0.85rem;
}
</style>
